import { BaseApi } from '../apiBase';
import { Account, AccountUpdatePayload, UpdatePasswordPayload, UpdatePasswordResponse, Designation } from '@portal/types/account.types';

export class AccountApi extends BaseApi {
  constructor() {
    super('/api/portal');
  }

  /**
   * Fetches the current account information
   */
  async getAccount(): Promise<Account> {
    const response = await this.get<Account>('/account');
    return response;
  }

  /**
   * Updates the current account information
   */
  async updateAccount(payload: AccountUpdatePayload): Promise<Account> {
    // Transform the payload to match Rails expectations
    const transformedPayload = {
      account: {
        email: payload.email,
        phone_opt_in_at: payload.phoneOptInAt,
        is_private: payload.isPrivate,
        profile_attributes: payload.profileAttributes ? {
          id: payload.profileAttributes.id,
          first_name: payload.profileAttributes.firstName,
          last_name: payload.profileAttributes.lastName,
          title: payload.profileAttributes.title,
          phone: payload.profileAttributes.phone,
          bio: payload.profileAttributes.bio,
          twitter_page: payload.profileAttributes.twitterPage,
          linkedin_page: payload.profileAttributes.linkedinPage,
          facebook_page: payload.profileAttributes.facebookPage,
          picture: payload.profileAttributes.picture,
          picture_direct_upload_key: payload.profileAttributes.pictureDirectUploadKey,
          color_hex: payload.profileAttributes.colorHex,
          remove_picture: payload.profileAttributes.removePicture,
          custom_attribute_values_attributes: payload.profileAttributes.customAttributeValuesAttributes
        } : undefined,
        designation_ids: payload.designationIds
      }
    };

    // If we have a file upload, we need to use FormData
    if (payload.profileAttributes?.picture instanceof File) {
      const formData = new FormData();

      // Add the picture file
      formData.append('account[profile_attributes][picture]', payload.profileAttributes.picture);

      // Add other fields
      if (payload.email) formData.append('account[email]', payload.email);
      if (payload.phoneOptInAt !== undefined) formData.append('account[phone_opt_in_at]', payload.phoneOptInAt || '');
      if (payload.isPrivate !== undefined) formData.append('account[is_private]', String(payload.isPrivate));

      // Add profile attributes
      if (payload.profileAttributes) {
        const attrs = payload.profileAttributes;
        if (attrs.id) formData.append('account[profile_attributes][id]', attrs.id);
        if (attrs.firstName) formData.append('account[profile_attributes][first_name]', attrs.firstName);
        if (attrs.lastName) formData.append('account[profile_attributes][last_name]', attrs.lastName);
        if (attrs.title !== undefined) formData.append('account[profile_attributes][title]', attrs.title || '');
        if (attrs.phone !== undefined) formData.append('account[profile_attributes][phone]', attrs.phone || '');
        if (attrs.bio !== undefined) formData.append('account[profile_attributes][bio]', attrs.bio || '');
        if (attrs.twitterPage !== undefined) formData.append('account[profile_attributes][twitter_page]', attrs.twitterPage || '');
        if (attrs.linkedinPage !== undefined) formData.append('account[profile_attributes][linkedin_page]', attrs.linkedinPage || '');
        if (attrs.facebookPage !== undefined) formData.append('account[profile_attributes][facebook_page]', attrs.facebookPage || '');
        if (attrs.removePicture) formData.append('account[profile_attributes][remove_picture]', 'true');
        
        // Add custom attribute values
        if (attrs.customAttributeValuesAttributes) {
          Object.entries(attrs.customAttributeValuesAttributes).forEach(([attrId, valueData]) => {
            if (valueData && typeof valueData === 'object' && 'value' in valueData) {
              if (Array.isArray(valueData.value)) {
                // Handle multiselect/checkbox values
                valueData.value.forEach((val, idx) => {
                  formData.append(`account[profile_attributes][custom_attribute_values_attributes][${attrId}][value][]`, val);
                });
              } else if (valueData.value !== undefined && valueData.value !== null) {
                formData.append(`account[profile_attributes][custom_attribute_values_attributes][${attrId}][value]`, String(valueData.value));
              }
              
              // Handle file key if present
              if ('fileKey' in valueData && valueData.fileKey) {
                formData.append(`account[profile_attributes][custom_attribute_values_attributes][${attrId}][file_key]`, valueData.fileKey);
              }
              
              // Handle remove file flag
              if ('removeFile' in valueData && valueData.removeFile) {
                formData.append(`account[profile_attributes][custom_attribute_values_attributes][${attrId}][remove_file]`, 'true');
              }
            }
          });
        }
      }

      // Add designation IDs
      if (payload.designationIds) {
        payload.designationIds.forEach((id, index) => {
          formData.append(`account[designation_ids][]`, id);
        });
      }

      return this.put<Account>('/account', formData, undefined, { contentType: 'multipart' });
    } else {
      // No file upload, use regular JSON
      return this.put<Account>('/account', transformedPayload);
    }
  }

  /**
   * Updates the account password
   */
  async updatePassword(payload: UpdatePasswordPayload): Promise<UpdatePasswordResponse> {
    return this.putSimpleJson<UpdatePasswordResponse>('/account/update_password', {
      account: {
        current_password: payload.currentPassword,
        new_password: payload.newPassword,
        password_confirmation: payload.passwordConfirmation
      }
    });
  }

  /**
   * Fetches available designations
   */
  async getDesignations(): Promise<Designation[]> {
    return this.get<Designation[]>('/designations');
  }
}

export const accountApi = new AccountApi();